import { useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import type { SurveysRespondentStrategyInput } from 'generated/resources/datatable.graphql'
import { SurveysEmployeesStrategyLegalEntitiesDocument } from 'generated/resources/graphql'

import { EntitySelectWrapper } from 'modules/core/components/EntitySelectWrapper'
import LabeledInput from 'modules/core/components/LabeledInput'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

interface Props {
  strategy: SurveysRespondentStrategyInput
  onChange: (ids: number[]) => void
}

export function LegalEntitiesStrategy({ strategy, onChange }: Props) {
  const [skip, setSkip] = useState(true)

  const { company } = useCurrentSession()
  const { data, loading } = useQuery(SurveysEmployeesStrategyLegalEntitiesDocument, {
    variables: {
      companyId: Number(company?.id),
    },
    skip,
  })

  const legalEntityIds = new Set(strategy.arguments || [])
  const legalEntities =
    data?.companies.legalEntities.map((legalEntity) => ({
      id: legalEntity.id,
      name: legalEntity.legalName,
    })) || []
  const selectedLegalEntities = legalEntities.filter((legalEntity) =>
    legalEntityIds.has(legalEntity.id)
  )

  return (
    <Box gap="s16">
      <EntitySelectWrapper
        label={i18n.t('surveys.configuration.participants.legal_entities_label')}
        hideLabel
        loading={loading}
        onOpen={() => {
          setSkip(false)
        }}
        entities={legalEntities}
        selectedEntities={selectedLegalEntities}
        value={selectedLegalEntities}
        searchPlaceholder={i18n.t('common.search_by')}
        selectAllLabel={i18n.t('common.select_all')}
        clearLabel={i18n.t('common.clear')}
        selectedLabel={i18n.t('common.selected')}
        notFoundSubtitle={i18n.t('factorial_one.collections.empty_states.no_results.description')}
        notFoundTitle={i18n.t('factorial_one.collections.empty_states.no_results.title')}
        placeholder={i18n.t('common.select')}
        selectedItemsCopy={i18n.t('common.selected')}
        multiple
        onSelectMultiple={(legalEntities) => {
          if (!legalEntities) {
            return
          }

          onChange(legalEntities.map((legalEntitie) => Number(legalEntitie.id)))
        }}
        singleSelector={false}
        groups={[]}
        selectedGroup=""
        onSelect={() => {}}
        onGroupChange={() => {}}
        expandedElements={[]}
        selectedGroupedEntities={selectedLegalEntities}
        onSelectExpandedElements={() => {}}
      >
        <Box flexDirection="row" width="full">
          <LabeledInput
            aria-label="employee-selector"
            label={i18n.t('surveys.configuration.participants.legal_entities_label')}
            value={undefined}
          >
            <Box
              paddingX="s16"
              paddingY="s8"
              flexDirection="row"
              height="s40"
              alignItems="center"
              width="full"
            >
              <Box flexDirection="row" width="full" gap="s6">
                <Box overflowX="hidden" textAlign="left">
                  <Text.Normal ellipsis color="grey1000">
                    {selectedLegalEntities.length > 0
                      ? `${selectedLegalEntities.length} ${i18n.t('common.selected')}`
                      : i18n.t('common.select')}
                  </Text.Normal>
                </Box>
              </Box>
              <Icon.Small icon="caret-down" />
            </Box>
          </LabeledInput>
        </Box>
      </EntitySelectWrapper>
    </Box>
  )
}
